import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  GitMerge, Search, Filter, CheckCircle, XCircle, Clock, 
  DollarSign, User, Eye, RefreshCw, FileText, Calendar,
  ArrowRight, Building2, Link2
} from 'lucide-react';

const AdminCommitments = () => {
  const [commitments, setCommitments] = useState([]);
  const [deals, setDeals] = useState({});
  const [filteredCommitments, setFilteredCommitments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCommitment, setSelectedCommitment] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterCommitments();
  }, [commitments, searchTerm, statusFilter]);

  const fetchData = async () => {
    try {
      const [commitmentsRes, dealsRes] = await Promise.all([
        adminAPI.getAllCommitments(),
        adminAPI.getAllDeals()
      ]);
      
      setCommitments(commitmentsRes.data);
      
      // Create deals lookup map
      const dealsMap = {};
      dealsRes.data.forEach(deal => {
        dealsMap[deal.id] = deal;
      });
      setDeals(dealsMap);
    } catch (error) {
      toast.error('Failed to load commitments');
    } finally {
      setLoading(false);
    }
  };

  const filterCommitments = () => {
    let filtered = [...commitments];
    
    if (searchTerm) {
      filtered = filtered.filter(c => {
        const deal = deals[c.deal_id];
        return (
          c.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.lsp_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (deal && deal.deal_nickname.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      });
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.commitment_status === statusFilter);
    }
    
    setFilteredCommitments(filtered);
  };

  const updateCommitmentStatus = async (commitmentId, newStatus) => {
    setUpdating(true);
    try {
      await adminAPI.updateCommitmentStatus(commitmentId, newStatus);
      toast.success(`Commitment ${newStatus.replace('_', ' ')}`);
      fetchData();
      setSelectedCommitment(null);
    } catch (error) {
      toast.error('Failed to update commitment status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      requested: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Clock },
      admin_approved: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
      admin_declined: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
      lsp_confirmed: { color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: Link2 },
      funds_verified: { color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: DollarSign },
    };
    
    const config = statusConfig[status] || statusConfig.requested;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout title="Manage Commitments">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Manage Commitments">
      <div className="space-y-6 animate-slide-in-up" data-testid="admin-commitments">
        {/* Filters */}
        <Card className="glass-effect border-slate-200 dark:border-slate-700">
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by deal name, LSP ID, or commitment ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="commitment-search"
                />
              </div>
              <div className="w-48">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="requested">Requested</SelectItem>
                    <SelectItem value="admin_approved">Approved</SelectItem>
                    <SelectItem value="admin_declined">Declined</SelectItem>
                    <SelectItem value="lsp_confirmed">LSP Confirmed</SelectItem>
                    <SelectItem value="funds_verified">Funds Verified</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={fetchData} variant="outline" className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Total</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{commitments.length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Pending</p>
              <p className="text-2xl font-bold text-blue-600">{commitments.filter(c => c.commitment_status === 'requested').length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Approved</p>
              <p className="text-2xl font-bold text-green-600">{commitments.filter(c => c.commitment_status === 'admin_approved').length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Declined</p>
              <p className="text-2xl font-bold text-red-600">{commitments.filter(c => c.commitment_status === 'admin_declined').length}</p>
            </CardContent>
          </Card>
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardContent className="py-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">Verified</p>
              <p className="text-2xl font-bold text-emerald-600">{commitments.filter(c => c.commitment_status === 'funds_verified').length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Commitments List */}
        <div className="space-y-4">
          {filteredCommitments.length === 0 ? (
            <Card className="glass-effect border-slate-200 dark:border-slate-700">
              <CardContent className="py-12 text-center">
                <GitMerge className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600 dark:text-slate-400">No commitments found</p>
                <p className="text-sm text-slate-500 mt-1">Commitments will appear here when LSPs commit to deals</p>
              </CardContent>
            </Card>
          ) : (
            filteredCommitments.map((commitment) => {
              const deal = deals[commitment.deal_id];
              
              return (
                <Card 
                  key={commitment.id} 
                  className="glass-effect border-slate-200 dark:border-slate-700 hover:shadow-lg transition-all"
                  data-testid={`commitment-card-${commitment.id}`}
                >
                  <CardContent className="py-4">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 gradient-success rounded-lg flex items-center justify-center">
                              <GitMerge className="w-5 h-5 text-white" />
                            </div>
                            <ArrowRight className="w-4 h-4 text-slate-400" />
                            <div className="w-10 h-10 gradient-primary rounded-lg flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-white" />
                            </div>
                          </div>
                          {getStatusBadge(commitment.commitment_status)}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm mt-3">
                          <div>
                            <p className="text-slate-500 dark:text-slate-400">Deal</p>
                            <p className="font-medium text-slate-900 dark:text-slate-100">
                              {deal ? deal.deal_nickname : commitment.deal_id.slice(0, 12) + '...'}
                            </p>
                          </div>
                          <div>
                            <p className="text-slate-500 dark:text-slate-400">LSP</p>
                            <p className="font-medium text-slate-900 dark:text-slate-100">
                              {commitment.lsp_id.slice(0, 12)}...
                            </p>
                          </div>
                          <div>
                            <p className="text-slate-500 dark:text-slate-400">Commitment Amount</p>
                            <p className="font-medium text-green-600">
                              ${commitment.commitment_amount?.toLocaleString() || 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedCommitment(selectedCommitment?.id === commitment.id ? null : commitment)}
                          data-testid={`view-commitment-${commitment.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {selectedCommitment?.id === commitment.id ? 'Hide' : 'Review'}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Expanded Review Panel */}
                    {selectedCommitment?.id === commitment.id && (
                      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 animate-slide-in-up">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Commitment Details</h4>
                            <div className="space-y-2 text-sm">
                              <p><span className="text-slate-500">Commitment ID:</span> <span className="text-slate-900 dark:text-slate-100">{commitment.id}</span></p>
                              <p><span className="text-slate-500">Deal ID:</span> <span className="text-slate-900 dark:text-slate-100">{commitment.deal_id}</span></p>
                              <p><span className="text-slate-500">LSP ID:</span> <span className="text-slate-900 dark:text-slate-100">{commitment.lsp_id}</span></p>
                              <p><span className="text-slate-500">Amount:</span> <span className="text-green-600 font-medium">${commitment.commitment_amount?.toLocaleString() || 'N/A'}</span></p>
                              <p><span className="text-slate-500">Created:</span> <span className="text-slate-900 dark:text-slate-100">{new Date(commitment.created_at).toLocaleString()}</span></p>
                            </div>
                            
                            {deal && (
                              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                <h5 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Associated Deal</h5>
                                <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                                  <p>{deal.deal_nickname}</p>
                                  <p>{deal.property_city}, {deal.property_state}</p>
                                  <p>Reserve Required: ${deal.reserve_required_amount?.toLocaleString()}</p>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Update Status</h4>
                            <div className="space-y-3">
                              <p className="text-sm text-slate-600 dark:text-slate-400">
                                Current Status: {getStatusBadge(commitment.commitment_status)}
                              </p>
                              
                              {commitment.commitment_status === 'requested' && (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    className="flex-1 bg-green-600 hover:bg-green-700"
                                    onClick={() => updateCommitmentStatus(commitment.id, 'admin_approved')}
                                    disabled={updating}
                                    data-testid={`approve-commitment-${commitment.id}`}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Approve Match
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="flex-1"
                                    onClick={() => updateCommitmentStatus(commitment.id, 'admin_declined')}
                                    disabled={updating}
                                    data-testid={`decline-commitment-${commitment.id}`}
                                  >
                                    <XCircle className="w-4 h-4 mr-1" />
                                    Decline
                                  </Button>
                                </div>
                              )}
                              
                              {commitment.commitment_status === 'admin_approved' && (
                                <Button
                                  size="sm"
                                  className="w-full gradient-success border-0"
                                  onClick={() => updateCommitmentStatus(commitment.id, 'funds_verified')}
                                  disabled={updating}
                                >
                                  <DollarSign className="w-4 h-4 mr-1" />
                                  Mark Funds Verified
                                </Button>
                              )}
                              
                              {['admin_declined', 'funds_verified'].includes(commitment.commitment_status) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full"
                                  onClick={() => updateCommitmentStatus(commitment.id, 'requested')}
                                  disabled={updating}
                                >
                                  <Clock className="w-4 h-4 mr-1" />
                                  Reset to Requested
                                </Button>
                              )}
                            </div>
                            
                            <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                              <p className="text-sm text-green-700 dark:text-green-400">
                                <strong>Workflow:</strong> Requested → Admin Approved → LSP Confirmed → Funds Verified
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminCommitments;
